import type React from "react"

import { useState, useRef } from "react"
import type { Asset } from "@/lib/types"
import type { FrontendAsset } from "@/lib/api-types"
import { useAssets } from "@/hooks/useAssets"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Trash2, Upload, Loader2 } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Slider } from "@/components/ui/slider"

interface AssetSidebarProps {
  assets: Asset[]
  selectedAssetId: string | null
  onSelectAsset: (id: string | null) => void
  onUpdateAsset: (id: string, updates: Partial<Asset>) => void
  onDeleteAsset: (id: string) => void
  onAddAsset?: (asset: Asset) => void
}

export function AssetSidebar({
  assets,
  selectedAssetId,
  onSelectAsset,
  onUpdateAsset,
  onDeleteAsset,
  onAddAsset,
}: AssetSidebarProps) {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { assets: systemAssets, uploadAsset } = useAssets()
  
  const selectedAsset = assets.find((a) => a.id === selectedAssetId)

  const handleDragStart = (e: React.DragEvent, asset: FrontendAsset) => {
    e.dataTransfer.setData("application/json", JSON.stringify(asset))
    e.dataTransfer.effectAllowed = "copy"
  }

  // ✅ Novo: Handler para adicionar asset ao toque/clique
  const handleAssetClick = (asset: FrontendAsset) => {
    const assetToAdd: Asset = {
      id: `asset-${Date.now()}`,
      name: asset.name,
      imageUrl: asset.imageUrl,
      x: 50,
      y: 50,
      z: 1,
      w: asset.w || 200,
      h: asset.h || 200,
      rotation: 0,
      backendAssetId: asset.id,
    }
    onAddAsset?.(assetToAdd)
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    try {
      const newAsset = await uploadAsset(file)
      if (newAsset) {
        // Adicionar asset ao canvas com dimensões padrão
        const assetToAdd: Asset = {
          id: `asset-${Date.now()}`,
          name: newAsset.name,
          imageUrl: newAsset.imageUrl,
          x: 50,
          y: 50,
          z: 1,
          w: newAsset.w || 200,
          h: newAsset.h || 200,
          rotation: 0,
          backendAssetId: newAsset.id, // Preservar ID do backend
        }
        // Aqui podemos emitir um evento ou chamar callback para adicionar ao canvas
        console.log("Asset adicionado:", assetToAdd)
        onAddAsset?.(assetToAdd)
      }
      setIsAddModalOpen(false)
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ""
      }
    }
  }

  return (
    <aside className="w-80 border-l bg-gradient-to-b from-purple-500/5 to-pink-500/5 backdrop-blur-sm flex flex-col h-screen">
      {/* Asset Library */}
      <div className="flex-1 overflow-auto p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold">Biblioteca de Assets</h2>
          <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="gap-1">
                <Upload className="w-4 h-4" />
                + Novo
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Adicionar Novo Asset</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="asset-file">Arquivo PNG</Label>
                  <Input
                    ref={fileInputRef}
                    id="asset-file"
                    type="file"
                    accept="image/png"
                    onChange={handleFileSelect}
                    disabled={isUploading}
                  />
                  <p className="text-xs text-muted-foreground">
                    Máximo 10MB, formato PNG
                  </p>
                </div>
                {isUploading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Enviando asset...
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Assets Grid */}
        <div className="grid grid-cols-2 gap-3">
          {systemAssets.map((asset) => (
            <div
              key={asset.id}
              draggable
              onDragStart={(e) => handleDragStart(e, asset)}
              onClick={() => handleAssetClick(asset)} // Adicionado onClick
              className="group relative rounded-lg border-2 border-dashed border-muted-foreground/25 hover:border-purple-500 hover:shadow-lg transition-all cursor-grab active:cursor-grabbing bg-background/50 backdrop-blur-sm p-2"
              title={asset.name}
            >
              {/* Aspect Ratio Container - Quadrado Responsivo */}
              <div className="aspect-square w-full flex items-center justify-center bg-muted/30 rounded overflow-hidden">
                <img
                  src={asset.imageUrl}
                  alt={asset.name}
                  className="w-full h-full object-contain pointer-events-none"
                  loading="lazy"
                />
              </div>
              
              {/* Asset Name - Truncated */}
              <p className="text-xs text-center mt-1 truncate text-muted-foreground group-hover:text-foreground transition-colors">
                {asset.name.length > 15
                  ? asset.name.substring(0, 12) + "..."
                  : asset.name}
              </p>

              {/* Drag Hint */}
              <div className="absolute inset-0 rounded-lg bg-purple-500/0 group-hover:bg-purple-500/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all pointer-events-none">
                <span className="text-[10px] font-semibold text-purple-600 dark:text-purple-400">
                  Arrastar
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {systemAssets.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Upload className="w-8 h-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">Nenhum asset criado</p>
            <p className="text-xs text-muted-foreground/75">
              Clique em "+ Novo" para adicionar
            </p>
          </div>
        )}
      </div>

      {/* Transform Controls */}
      {selectedAsset && (
        <div className="border-t p-4 space-y-4 bg-background/50 backdrop-blur-sm max-h-96 overflow-auto">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">Asset Selecionado</h3>
            <Button size="sm" variant="ghost" onClick={() => onDeleteAsset(selectedAsset.id)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="x" className="text-xs">
                  X (horizontal)
                </Label>
                <Input
                  id="x"
                  type="number"
                  value={Math.round(selectedAsset.x)}
                  onChange={(e) => onUpdateAsset(selectedAsset.id, { x: Number(e.target.value) })}
                  className="h-8"
                />
              </div>
              <div>
                <Label htmlFor="y" className="text-xs">
                  Y (vertical)
                </Label>
                <Input
                  id="y"
                  type="number"
                  value={Math.round(selectedAsset.y)}
                  onChange={(e) => onUpdateAsset(selectedAsset.id, { y: Number(e.target.value) })}
                  className="h-8"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label htmlFor="w" className="text-xs">
                  Largura
                </Label>
                <Input
                  id="w"
                  type="number"
                  value={Math.round(selectedAsset.w)}
                  onChange={(e) => onUpdateAsset(selectedAsset.id, { w: Number(e.target.value) })}
                  className="h-8"
                />
              </div>
              <div>
                <Label htmlFor="h" className="text-xs">
                  Altura
                </Label>
                <Input
                  id="h"
                  type="number"
                  value={Math.round(selectedAsset.h)}
                  onChange={(e) => onUpdateAsset(selectedAsset.id, { h: Number(e.target.value) })}
                  className="h-8"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="width-slider" className="text-xs">
                Redimensionar Largura: {Math.round(selectedAsset.w)}px
              </Label>
              <Slider
                id="width-slider"
                min={20}
                max={2000}
                step={10}
                value={[selectedAsset.w]}
                onValueChange={(value) => onUpdateAsset(selectedAsset.id, { w: value[0] })}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="height-slider" className="text-xs">
                Redimensionar Altura: {Math.round(selectedAsset.h)}px
              </Label>
              <Slider
                id="height-slider"
                min={20}
                max={2000}
                step={10}
                value={[selectedAsset.h]}
                onValueChange={(value) => onUpdateAsset(selectedAsset.id, { h: value[0] })}
                className="w-full"
              />
            </div>

            <div>
              <Label htmlFor="z" className="text-xs">
                Camada (Z)
              </Label>
              <Input
                id="z"
                type="number"
                value={selectedAsset.z}
                onChange={(e) => onUpdateAsset(selectedAsset.id, { z: Number(e.target.value) })}
                className="h-8"
              />
            </div>

            <div>
              <Label htmlFor="rotation" className="text-xs">
                Rotação (graus)
              </Label>
              <Input
                id="rotation"
                type="number"
                min="0"
                max="360"
                value={selectedAsset.rotation || 0}
                onChange={(e) => onUpdateAsset(selectedAsset.id, { rotation: Number(e.target.value) })}
                className="h-8"
              />
            </div>
          </div>
        </div>
      )}
    </aside>
  )
}

